import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpDown, ArrowUp, ArrowDown, MapPin, ChevronRight } from "lucide-react";
import { AssetView, AssetCategoryView, AssetStatus, AssetCondition } from "../../../../api/General/assets";
import { Card, CardContent } from "@/components/ui/Card";
import { Tooltip } from "@/components/ui/Tooltip";

interface AssetTableProps {
  assets: AssetView[];
  categories: AssetCategoryView[];
  loading: boolean;
  onRowClick: (asset: AssetView) => void;
  sortBy: string;
  sortDescending: boolean;
  onSort: (field: string) => void;
}

const statusStyles: Record<AssetStatus, string> = {
  [AssetStatus.Available]: "bg-green-100 text-green-800",
  [AssetStatus.Issued]: "bg-blue-100 text-blue-800",
  [AssetStatus.UnderMaintenance]: "bg-yellow-100 text-yellow-800",
  [AssetStatus.Retired]: "bg-muted text-muted-foreground",
};

const conditionStyles: Record<AssetCondition, string> = {
  [AssetCondition.Excellent]: "bg-emerald-100 text-emerald-800",
  [AssetCondition.Good]: "bg-blue-100 text-blue-800",
  [AssetCondition.Fair]: "bg-orange-100 text-orange-800",
  [AssetCondition.Poor]: "bg-red-100 text-red-800",
};

// Deeper levels step in by one notch each; the array keeps the classes literal for Tailwind.
const pathIndent = ["", "ps-2", "ps-4", "ps-6"];

// The category chain as a stack, parents muted above the level the asset actually sits in.
const CategoryPath: React.FC<{ levels: string[] }> = ({ levels }) => (
  <span className="flex flex-col gap-0.5">
    {levels.map((name, i) => (
      <span
        key={`${name}-${i}`}
        className={`flex items-center ${pathIndent[Math.min(i, pathIndent.length - 1)]} ${
          i === levels.length - 1 ? "font-medium text-foreground" : "text-muted-foreground"
        }`}
      >
        {i > 0 && (
          <ChevronRight className="w-3 h-3 me-1 shrink-0 text-muted-foreground/60 rtl:rotate-180" aria-hidden="true" />
        )}
        {name}
      </span>
    ))}
  </span>
);

const formatValue = (price?: number | null): string =>
  price != null
    ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price)
    : "-";

const AssetTable: React.FC<AssetTableProps> = ({
  assets,
  categories,
  loading,
  onRowClick,
  sortBy,
  sortDescending,
  onSort,
}) => {
  const { t } = useTranslation();

  // Resolve a category's full ancestor chain (e.g. Electronics / Laptops / Dell Latitude 5420)
  // from the loaded category tree, so the table can show all three levels without a wide column.
  const categoryById = useMemo(() => {
    const map = new Map<number, AssetCategoryView>();
    for (const c of categories) map.set(c.id, c);
    return map;
  }, [categories]);

  const resolveCategory = (asset: AssetView): { ancestors: string[] } => {
    const chain: string[] = [];
    let cur = categoryById.get(asset.categoryId);
    let guard = 0;
    while (cur && guard < 12) {
      chain.unshift(cur.name);
      cur = cur.parentId != null ? categoryById.get(cur.parentId) : undefined;
      guard++;
    }
    // Category tree not resolved locally (e.g. still loading) — fall back to the flat name from the API.
    if (chain.length === 0) return { ancestors: asset.categoryName ? [asset.categoryName] : [] };
    return { ancestors: chain.slice(0, -1) };
  };

  const statusLabel = (s: AssetStatus) => {
    switch (s) {
      case AssetStatus.Available: return t("assets.statusAvailable");
      case AssetStatus.Issued: return t("assets.statusIssued");
      case AssetStatus.UnderMaintenance: return t("assets.statusUnderMaintenance");
      case AssetStatus.Retired: return t("assets.statusRetired");
      default: return "";
    }
  };

  const conditionLabel = (c: AssetCondition) => {
    switch (c) {
      case AssetCondition.Excellent: return t("assets.conditionExcellent");
      case AssetCondition.Good: return t("assets.conditionGood");
      case AssetCondition.Fair: return t("assets.conditionFair");
      case AssetCondition.Poor: return t("assets.conditionPoor");
      default: return "";
    }
  };

  const SortIcon: React.FC<{ field: string }> = ({ field }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="ms-1 w-3.5 h-3.5 text-muted-foreground/60" aria-hidden="true" />;
    }
    return sortDescending
      ? <ArrowDown className="ms-1 w-3.5 h-3.5 text-foreground" aria-hidden="true" />
      : <ArrowUp className="ms-1 w-3.5 h-3.5 text-foreground" aria-hidden="true" />;
  };

  // Only columns the backend can sort (name/category/value/status/condition) are clickable.
  // `hint` is set only where the label alone leaves the meaning open.
  const columns: { key: string; label: string; sortable: boolean; hint?: string }[] = [
    { key: "name", label: t("assets.name"), sortable: true },
    { key: "category", label: t("assets.category"), sortable: true, hint: t("assets.colCategoryTooltip") },
    { key: "location", label: t("assets.location"), sortable: false },
    { key: "value", label: t("assets.value"), sortable: true, hint: t("assets.colValueTooltip") },
    { key: "status", label: t("assets.status"), sortable: true, hint: t("assets.colStatusTooltip") },
    { key: "condition", label: t("assets.condition"), sortable: true, hint: t("assets.colConditionTooltip") },
    { key: "assignedTo", label: t("assets.assignedTo"), sortable: false },
  ];

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-8 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <svg className="w-12 h-12 mx-auto mb-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
        <p className="text-muted-foreground text-lg font-medium">{t("assets.noAssets")}</p>
        <p className="text-muted-foreground text-sm mt-1">{t("assets.noAssetsDescription")}</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden sm:block">
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="asset-table">
              <thead>
                <tr className="border-b border-border bg-muted">
                  {columns.map((col) => {
                    // A real button so the column can be sorted from the keyboard and so the
                    // hint is reachable by tabbing; its click bubbles to the cell's sort handler.
                    const heading = col.sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        {col.label}
                        <SortIcon field={col.key} />
                      </button>
                    ) : (
                      <span
                        tabIndex={col.hint ? 0 : undefined}
                        className="inline-flex items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        {col.label}
                      </span>
                    );
                    return (
                      <th
                        key={col.key}
                        scope="col"
                        aria-sort={
                          col.sortable
                            ? sortBy === col.key
                              ? sortDescending ? "descending" : "ascending"
                              : "none"
                            : undefined
                        }
                        className={`px-4 py-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wider select-none ${
                          col.sortable ? "cursor-pointer hover:bg-muted" : ""
                        }`}
                        onClick={col.sortable ? () => onSort(col.key) : undefined}
                      >
                        {col.hint ? <Tooltip content={col.hint}>{heading}</Tooltip> : heading}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {assets.map((asset) => {
                  const cat = resolveCategory(asset);
                  const locationName = asset.storageLocationName ?? asset.location ?? null;
                  return (
                    <tr
                      key={asset.id}
                      className="hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => onRowClick(asset)}
                      data-testid={`asset-row-${asset.id}`}
                    >
                      {/* Asset name + serial */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">{asset.name}</span>
                          {asset.serialNumber && (
                            <code className="mt-0.5 text-xs font-mono text-muted-foreground">{asset.serialNumber}</code>
                          )}
                        </div>
                      </td>
                      {/* Category (Group / Type) — the Model itself is shown as the asset Name above */}
                      <td className="px-4 py-3">
                        {cat.ancestors.length > 0 ? (
                          <Tooltip content={<CategoryPath levels={cat.ancestors} />} className="max-w-[220px]">
                            <span
                              tabIndex={0}
                              className="block min-w-0 truncate rounded-sm text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                            >
                              {cat.ancestors.join(" / ")}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      {/* Location */}
                      <td className="px-4 py-3">
                        {locationName ? (
                          <Tooltip content={locationName}>
                            <span
                              tabIndex={0}
                              className="inline-flex max-w-[180px] items-center gap-1.5 rounded-sm text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                            >
                              <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
                              <span className="min-w-0 truncate">{locationName}</span>
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </td>
                      {/* Value */}
                      <td className="px-4 py-3 text-sm text-foreground">{formatValue(asset.purchasePrice)}</td>
                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[asset.status]}`}>
                          {statusLabel(asset.status)}
                        </span>
                      </td>
                      {/* Condition */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${conditionStyles[asset.condition]}`}>
                          {conditionLabel(asset.condition)}
                        </span>
                      </td>
                      {/* Assigned to */}
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {asset.assignedToUserName ?? "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile card fallback */}
      <div className="sm:hidden space-y-3">
        {assets.map((asset) => {
          const cat = resolveCategory(asset);
          const locationName = asset.storageLocationName ?? asset.location ?? null;
          return (
            <Card
              key={asset.id}
              variant="interactive"
              onClick={() => onRowClick(asset)}
              className="cursor-pointer"
              data-testid={`asset-row-${asset.id}`}
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-medium text-foreground block truncate">{asset.name}</span>
                    {asset.serialNumber && (
                      <code className="text-xs font-mono text-muted-foreground">{asset.serialNumber}</code>
                    )}
                  </div>
                  <span className={`shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[asset.status]}`}>
                    {statusLabel(asset.status)}
                  </span>
                </div>

                {/* Category (Group / Type) — the Model itself is shown as the asset Name above */}
                {cat.ancestors.length > 0 && (
                  <div className="text-sm text-muted-foreground">{cat.ancestors.join(" / ")}</div>
                )}

                {/* Location */}
                {locationName && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate">{locationName}</span>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${conditionStyles[asset.condition]}`}>
                    {conditionLabel(asset.condition)}
                  </span>
                  {asset.purchasePrice != null && (
                    <span className="text-muted-foreground">{formatValue(asset.purchasePrice)}</span>
                  )}
                </div>

                {asset.assignedToUserName && (
                  <div className="text-xs text-muted-foreground">{t("assets.assignedTo")}: {asset.assignedToUserName}</div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};

export default AssetTable;
